import { BigNumber } from 'bignumber.js';
import { match } from 'ts-pattern';

import { expandDecimals } from './expand-decimals';
import { formatCompactNumberWithPrefix } from './format-compact-number';

BigNumber.config({ EXPONENTIAL_AT: 666 });

const COMPACT_NUMBER_THRESHOLD = 1_000_000_000;
const LEADING_ZEROES_OFFSET = 4;
const LEADING_ZEROES_THRESHOLD = 1;

const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;
const TRILLION = 1_000_000_000_000;

const parseLeadingDecimalZeros = (value: BigNumber) => {
  const decimalValue = value.mod(1);
  const decimalCount = value.dp();

  if (!decimalCount) {
    return { leadingZeroCount: 0, decimalFragment: '' };
  }

  const leadingZeroCount = decimalCount - expandDecimals(decimalCount, decimalValue.toString()).length;

  return {
    leadingZeroCount,
    decimalFragment: new BigNumber(expandDecimals(leadingZeroCount + LEADING_ZEROES_OFFSET, decimalValue.toString()))
      .dp(0)
      .toString()
      .replace(/0+$/g, ''),
  };
};

export const formatSupplyCompactNumber = (value: number): string =>
  match(value)
    .when(
      (v) => v < THOUSAND,
      (v) => v.toString(),
    )
    .when(
      (v) => v < MILLION,
      (v) => `${Math.round((v / THOUSAND) * 10) / 10}k`,
    )
    .when(
      (v) => v < BILLION,
      (v) => `${Math.round((v / MILLION) * 10) / 10}M`,
    )
    .when(
      (v) => v < TRILLION,
      (v) => `${Math.round((v / BILLION) * 10) / 10}B`,
    )
    .otherwise((v) => `${Math.round((v / TRILLION) * 10) / 10}T`);

const getSubscriptUnicode = (value: number): string => {
  if (!Number.isInteger(value) || value < 0) {
    return '';
  }

  const unicode = ['\u2080', '\u2081', '\u2082', '\u2083', '\u2084', '\u2085', '\u2086', '\u2087', '\u2088', '\u2089'];

  return value
    .toString()
    .split('')
    .reduce((subscript, digit) => `${subscript}${unicode[Number(digit)]}`, '');
};

const removeTrailingZeros = (value: string): string => value.replace(/00$/, '');

export const formatNumberWithPrefix =
  (prefix: string) =>
  (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    const valueBn = new BigNumber(value).abs();
    const isNegative = new BigNumber(value).isNegative();

    if (valueBn.gt(COMPACT_NUMBER_THRESHOLD)) {
      return formatCompactNumberWithPrefix(prefix, value);
    }

    if (valueBn.gt(1)) {
      const format = {
        prefix: `${isNegative ? '-' : ''}${prefix}`,
        decimalSeparator: '.',
        groupSeparator: ',',
        groupSize: 3,
      };

      return valueBn.toFormat(2, BigNumber.ROUND_DOWN, format);
    }

    const { leadingZeroCount, decimalFragment } = parseLeadingDecimalZeros(valueBn);

    if (leadingZeroCount < LEADING_ZEROES_THRESHOLD) {
      const formattedValue =
        leadingZeroCount === 0
          ? valueBn.toFixed(Math.min(LEADING_ZEROES_OFFSET, Math.max(2, valueBn.dp() ?? 2)))
          : valueBn.dp(leadingZeroCount + LEADING_ZEROES_OFFSET, BigNumber.ROUND_DOWN).toString();

      const normalizedValue =
        (valueBn.dp() ?? 0) >= LEADING_ZEROES_OFFSET ? removeTrailingZeros(formattedValue) : formattedValue;

      return `${isNegative ? '-' : ''}${prefix}${normalizedValue}`;
    }

    return `${isNegative ? '-' : ''}${prefix}0.0${getSubscriptUnicode(leadingZeroCount)}${decimalFragment}`;
  };

export const formatNumber = formatNumberWithPrefix('');
